import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { LANGS, STRINGS } from "@/i18n/strings";

const LanguageContext = createContext(null);
const STORAGE_KEY = "tfj-lang-v2";
const DEFAULT_LANG = "nb";

const initialLang = () => {
  const saved = localStorage.getItem(STORAGE_KEY);
  return STRINGS[saved] ? saved : DEFAULT_LANG;
};

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(initialLang);
  const meta = LANGS.find((l) => l.code === lang);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, lang);
    document.documentElement.lang = meta.html;
    document.title = STRINGS[lang].meta.title;
  }, [lang, meta]);

  const fmtNum = useCallback(
    (n, digits = 0) =>
      new Intl.NumberFormat([meta.locale, "nb-NO"], { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(n),
    [meta]
  );

  const fmtDate = useCallback(
    (d) => {
      if (lang === "nb" || lang === "se") {
        const pad = (v) => String(v).padStart(2, "0");
        return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
      }
      const month = lang === "zh" ? "long" : "short";
      return d.toLocaleDateString(meta.locale, { day: "numeric", month, year: "numeric" });
    },
    [lang, meta]
  );

  // Localized troll text field (epithet / blurb), English as fallback.
  const tl = useCallback((troll, field) => troll[`${field}_${lang}`] || troll[`${field}_en`], [lang]);

  const regionName = useMemo(() => {
    const own = STRINGS[lang].emergency.countries;
    const dn = new Intl.DisplayNames([meta.locale, "nb-NO"], { type: "region" });
    return (code, fallback) => own?.[code] || dn.of(code) || fallback;
  }, [lang, meta]);

  const value = useMemo(
    () => ({ lang, setLang, t: STRINGS[lang], locale: meta.locale, fmtNum, fmtDate, tl, regionName }),
    [lang, meta, fmtNum, fmtDate, tl, regionName]
  );
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export const useLang = () => useContext(LanguageContext);
